// Modules
import { useCallback, useContext, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import axios from 'axios'

// Hooks
import useNotices from 'hooks/system/useNotices'
import useToken from 'hooks/authentication/useToken'

// Context
import Data from 'context/Data'
import Dispatch from 'context/Dispatch'

// Reducers
import { ACCOUNT_ACTIONS } from 'reducers/account'

// Helpers
import { getAuthorizationHeader, getTeam, logout, storeTeam } from 'helpers/authentication'

// Queries
import ACCOUNT from 'queries/account/Account'

// Constants
import { TERMS_OF_SERVICE_UPDATE_DATE } from 'constants/content'
import { ACCOUNT_DATA_URL } from 'constants/environment'

// Hook: Account > Account
function useAccount (isRoot = false) {
	// Data
	const token = useToken()
	const { addNotice } = useNotices()
	const { account } = useContext(Data)
	const dispatch = useContext(Dispatch)
	const { data: accountData, error: accountError } = useQuery(ACCOUNT, {
		fetchPolicy: 'no-cache',
		skip: !isRoot || !!account || !token
	})

	// Functions (Memoized)
	const updateAccount = useCallback(data => {
		dispatch({
			type: ACCOUNT_ACTIONS.SET,
			payload: {
				...(data || {})
			}
		})
	}, [dispatch])

	// Effects
	useEffect(() => {
		async function setTermsAcceptingData() {
			const { data } = await axios({
				headers: { ...getAuthorizationHeader() },
				method: 'GET',
				url: ACCOUNT_DATA_URL
			})
			if (!!data) {
				updateAccount({
					isLatestTermsOfServiceAccepted: (
						!!data.termsAcceptedAt &&
						!(new Date(data.termsAcceptedAt.replace('Z', '')).getTime() < new Date(TERMS_OF_SERVICE_UPDATE_DATE).getTime())
					)
				})
			} else addNotice({ code: 'client.termsAcceptingError', type: 'error' })
		}
		if (!!account && typeof account.isLatestTermsOfServiceAccepted !== 'boolean') setTermsAcceptingData()
	}, [account, addNotice, updateAccount])
	useEffect(() => {
		if (!!accountData?.user) {
			const defaultTeamId = accountData.user.team?.id
			const storedTeamId = getTeam()
			const teamId = !!storedTeamId && accountData.user.teams?.some(team => team.id.toString() === storedTeamId.toString()) ? storedTeamId : defaultTeamId // Used toString on both side to avoid possible problems caused by a change in BE implementation
			storeTeam(teamId)
			updateAccount({
				...(accountData.user || {}),
				belespritOnboardingTime: accountData.user.belEspritOnboardedAt,
				isPhoneNumberVerified: !!accountData.user.phoneNumberVerifiedAt,
				isPhoneNumberVerifiedBefore: !!accountData.user.phoneNumberVerifiedBefore,
				team: { id: teamId } // Current team
			})
		}
	}, [accountData?.user, updateAccount])
	useEffect(() => {
		if (isRoot && !account && !!accountError) logout()
	}, [account, accountError, isRoot])
}

// Export
export default useAccount
